import { Box, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { ReactElement, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Row } from 'react-table';
import { QueryActivityPageBoomerang } from '../../../api/schema';
import { CopyTsvButton } from '../../../component/copyTsvButton/CopyTsvButton';
import Table from '../../../component/table/Table';
import { ACTIVITY_COLORS } from './activityColors';

type BoomerangTablesProps = {
  real: QueryActivityPageBoomerang[];
  artifact: QueryActivityPageBoomerang[];
  tz: string;
  fetching: boolean;
};

type RowData = {
  key: string;
  site: string;
  site_id: string;
  job_number: string;
  identifier: string;
  item_id: string;
  product: string;
  out_at: string;
  back_at: string;
  gap: string;
};

/** "3h 20m", or "45m" under an hour: how long the machine was actually gone. */
const formatGap = (minutes?: number): string => {
  const total = Math.max(0, Math.round(minutes || 0));
  const hours = Math.floor(total / 60);
  return hours ? `${hours}h ${total % 60}m` : `${total}m`;
};

const toRows = (rows: QueryActivityPageBoomerang[], tz: string): RowData[] =>
  rows.map((row, index) => ({
    key: `${row.item_id}-${row.out_at}-${index}`,
    site: row.site_name || '',
    site_id: row.site_id || '',
    job_number: row.job_number || '',
    identifier: row.item_identifier || '',
    item_id: row.item_id || '',
    product: row.product_name || '',
    out_at: DateTime.fromISO(row.out_at || '', { zone: tz }).toFormat('MMM d, HH:mm'),
    back_at: DateTime.fromISO(row.back_at || '', { zone: tz }).toFormat('MMM d, HH:mm'),
    gap: formatGap(row.gap_minutes),
  }));

const BoomerangTable = ({
  rows,
  title,
  colour,
  explanation,
  fetching,
  emptyText,
}: {
  rows: RowData[];
  title: string;
  colour: string;
  explanation: string;
  fetching: boolean;
  emptyText: string;
}): ReactElement => {
  const columns = useMemo(
    () => [
      {
        Header: 'Job Site',
        accessor: 'site',
        ellipsis: true,
        disableFilters: true,
        Cell: ({ row }: { row: { original: RowData } }) => <Link to={`/customers/job-sites/${row.original.site_id}/details`}>{row.original.site}</Link>,
      },
      { Header: 'Job #', accessor: 'job_number', disableFilters: true },
      {
        Header: 'Item',
        accessor: 'identifier',
        disableFilters: true,
        Cell: ({ row }: { row: { original: RowData } }) => <Link to={`/tools/items/${row.original.item_id}/details`}>{row.original.identifier}</Link>,
      },
      { Header: 'Product', accessor: 'product', ellipsis: true, disableFilters: true },
      { Header: 'Out', accessor: 'out_at', disableFilters: true },
      { Header: 'Back', accessor: 'back_at', disableFilters: true },
      { Header: 'Gone for', accessor: 'gap', disableFilters: true },
    ],
    [],
  );

  return (
    <Box sx={{ mb: 4 }}>
      <Table<RowData>
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: colour }} />
            <Typography variant="h6">{title}</Typography>
          </Stack>
        }
        columns={columns}
        data={rows}
        loading={fetching}
        size="small"
        withPagination={false}
        noGlobalFilter
        rowIdKey="key"
        noDataText={emptyText}
        // The row opens the job site; the item cell keeps its own link, which
        // Table honours because it skips clicks that landed on an anchor.
        getRowProps={(row: Row) => ({ link: `/customers/job-sites/${(row.original as RowData).site_id}/details` })}
        headerAddon={
          <CopyTsvButton
            label={title}
            header={['Job Site', 'Job #', 'Item', 'Product', 'Out', 'Back', 'Gone for']}
            rows={rows.map((row) => [row.site, row.job_number, row.identifier, row.product, row.out_at, row.back_at, row.gap])}
            disabled={fetching || !rows.length}
          />
        }
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {explanation}
      </Typography>
    </Box>
  );
};

/**
 * The equipment behind the two boomerang counts, in two tables.
 *
 * Deliberately never one table with a class column, and never a combined total.
 * A return is an operations problem -- too much equipment sent out -- and a
 * cancellation is a data-entry problem. Reading them as one number hides both.
 */
export const BoomerangTables = ({ real, artifact, tz, fetching }: BoomerangTablesProps): ReactElement => (
  <>
    <BoomerangTable
      rows={toRows(real, tz)}
      title="Returns"
      colour={ACTIVITY_COLORS.boomerangReal}
      explanation="Delivered and recovered the same day, hours apart: the equipment most likely reached the site and came back unused. Not billable, and a sign of over-allocation."
      fetching={fetching}
      emptyText="No returns in this period"
    />
    <BoomerangTable
      rows={toRows(artifact, tz)}
      title="Cancellations"
      colour={ACTIVITY_COLORS.boomerangArtifact}
      explanation="Delivered and recovered within two hours: the equipment never left the building. These are mis-clicks and retries, not movements: each unit is listed once per two-hour window, on its first delivery and the first scan after it, and they are excluded from every other number on this page."
      fetching={fetching}
      emptyText="No cancellations in this period"
    />
  </>
);

export default BoomerangTables;
